import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import { faChevronUp, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { faPlusSquare, faMinusSquare } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DataTable from "@components/DataTable";
import SlideoutMenu from "@components/SlideOutMenuV2/SlideOutMenu";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import SubmitButton from "@components/Buttons/SubmitButton/SubmitButton";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { AdminFormPage } from "@components/CatalogIndex/AdminFormPage";
import { PsPricingRow } from "../types/PsPricingRow";
import useStyles from "../styles/useStyles";

interface TableSection {
  title: string;
  rows: PsPricingRow[];
}

interface ByLobData {
  profRows: PsPricingRow[];
  lobTables: TableSection[];
  govRows: PsPricingRow[];
}

interface ByPhaseData {
  phaseTables: TableSection[];
  govRows: PsPricingRow[];
}

interface ByServiceData {
  phaseTables: TableSection[];
  govRows: PsPricingRow[];
}

interface ByLocationData {
  locTables: TableSection[];
}

interface DataTableRow extends PsPricingRow {
  collapsedData?: React.ReactNode;
  parentServiceId?: string;
  hasDetails?: boolean;
}

interface Props {
  currencyUnit: string;
  rateType: string;
  projectId: string;
  projectStatus: string;
  pricingPrivilege: boolean;
  oneTimeAdjustment: number;
  adjustmentError: string[] | null;
  setAdjustmentError: (v: string[] | null) => void;
  paymentTerms: any[];
  currentPaymentTerm: any;
  newPaymentTermId: string | null;
  setNewPaymentTermId: (v: string | null) => void;
  onSubmitPaymentTerm: () => void;
  paymentTermError: string[] | null;
  setPaymentTermError: (v: string[] | null) => void;
  byLobData: ByLobData;
  byPhaseData: ByPhaseData;
  byServiceData: ByServiceData;
  byLocationData: ByLocationData;
  showServiceTab: boolean;
  showLocationTab: boolean;
  isLoading: boolean;
}

const Show: React.FC<Props> = ({
  currencyUnit,
  rateType,
  projectId,
  projectStatus,
  pricingPrivilege,
  oneTimeAdjustment,
  adjustmentError,
  setAdjustmentError,
  paymentTerms,
  currentPaymentTerm,
  newPaymentTermId,
  setNewPaymentTermId,
  onSubmitPaymentTerm,
  paymentTermError,
  setPaymentTermError,
  byLobData,
  byPhaseData,
  byServiceData,
  byLocationData,
  showServiceTab,
  showLocationTab,
  isLoading,
}) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState("byLob");
  const [showTerms, setShowTerms] = useState(false);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const [expandedServices, setExpandedServices] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? true),
    }));
  };

  const toggleService = (id: string) => {
    setExpandedServices((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const isSectionExpanded = (id: string) => expandedSections[id] ?? true;

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={value.toFixed(2)}
    />
  );

  const hoursLabel = rateType !== "Hours" ? rateType : "Extended Hours";

  const buildColumns = (
    firstColumnName: string,
    showAdjustment = false
  ): ColumnDef<DataTableRow>[] => [
    {
      id: "resource",
      header: firstColumnName,
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment")
          return (
            <span>
              Price Adjustments (See{" "}
              <Link
                to={`/projects/${projectId}/project_pricing`}
                style={{ color: "#418172", fontWeight: 700 }}
              >
                Overall Pricing
              </Link>
              )
            </span>
          );
        if (row.rowType === "total") return null;
        if (row.rowType === "margin") return null;
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700, paddingLeft: 20 }}>
              {row.resource}
            </span>
          );
        return (
          <span
            style={{
              paddingLeft: row.rowType === "subservice" ? 20 : 0,
            }}
          >
            {row.rowType === "subservice" ? `- ${row.resource}` : row.resource}
          </span>
        );
      },
    },
    {
      id: "hours",
      header: hoursLabel,
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment") return null;
        if (row.rowType === "margin") return null;
        if (row.rowType === "total" || row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {row.hours > 0 ? row.hours.toFixed(2) : ""}
            </span>
          );
        return <span>{row.hours > 0 ? row.hours.toFixed(2) : ""}</span>;
      },
    },
    {
      id: "rate",
      header: "Effective Rate",
      cell(props) {
        const row = props.row.original;
        if (
          row.rowType === "adjustment" ||
          row.rowType === "margin" ||
          row.rowType === "total" ||
          row.rowType === "serviceTotal"
        )
          return null;
        if (row.revenue === 0 || row.hours === 0 || isNaN(row.rate))
          return <span>---</span>;
        return <span>{formatCurrency(row.rate)}</span>;
      },
    },
    {
      id: "revenue",
      header: "Revenue",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment") {
          return <span>{formatCurrency(oneTimeAdjustment)}</span>;
        }
        if (row.rowType === "margin") return null;
        if (row.rowType === "total" || row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.revenue)}
            </span>
          );
        return <span>{formatCurrency(row.revenue)}</span>;
      },
    },
    {
      id: "effectiveCost",
      header: "Effective Cost",
      cell(props) {
        const row = props.row.original;
        if (
          row.rowType === "adjustment" ||
          row.rowType === "margin" ||
          row.rowType === "total" ||
          row.rowType === "serviceTotal"
        )
          return null;
        if (row.totalCost === 0 || row.hours === 0 || isNaN(row.effectiveCost))
          return <span>---</span>;
        return <span>{formatCurrency(row.effectiveCost)}</span>;
      },
    },
    {
      id: "totalCost",
      header: "Total Cost",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment" || row.rowType === "margin")
          return null;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#b90200" }}>
              {formatCurrency(row.totalCost)}
            </span>
          );
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.totalCost)}
            </span>
          );
        return <span>{formatCurrency(row.totalCost)}</span>;
      },
    },
    {
      id: "grossProfit",
      header: "Gross Profit",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment")
          return <span>{formatCurrency(oneTimeAdjustment)}</span>;
        if (row.rowType === "margin")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {row.revenue ? `${row.revenue.toFixed(2)}%` : "0.00%"}
            </span>
          );
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {formatCurrency(row.grossProfit)}
            </span>
          );
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.grossProfit)}
            </span>
          );
        return <span>{formatCurrency(row.grossProfit)}</span>;
      },
    },
  ];

  const buildServiceColumns = (): ColumnDef<DataTableRow>[] => [
    {
      id: "resource",
      header: "Service",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total") return null;
        if (row.hasDetails) {
          const isExpanded = expandedServices[row.id] || false;
          return (
            <span
              style={{ cursor: "pointer" }}
              onClick={() => toggleService(row.id)}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faMinusSquare : faPlusSquare}
                style={{ marginRight: 8, color: "#6c757d" }}
              />
              {row.resource}
            </span>
          );
        }
        if (row.parentServiceId) {
          if (row.rowType === "subservice")
            return <span style={{ paddingLeft: 40 }}>- {row.resource}</span>;
          return <span style={{ paddingLeft: 20 }}>{row.resource}</span>;
        }
        return <span>{row.resource}</span>;
      },
    },
    {
      id: "hours",
      header: hoursLabel,
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700 }}>
              {row.hours > 0 ? row.hours.toFixed(2) : ""}
            </span>
          );
        return <span>{row.hours > 0 ? row.hours.toFixed(2) : ""}</span>;
      },
    },
    {
      id: "rate",
      header: "Effective Rate",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total") return null;
        if (row.revenue === 0 || row.hours === 0 || isNaN(row.rate))
          return <span>---</span>;
        return <span>{formatCurrency(row.rate)}</span>;
      },
    },
    {
      id: "revenue",
      header: "Revenue",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.revenue)}
            </span>
          );
        return <span>{formatCurrency(row.revenue)}</span>;
      },
    },
    {
      id: "effectiveCost",
      header: "Effective Cost",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total") return null;
        if (row.totalCost === 0 || row.hours === 0 || isNaN(row.effectiveCost))
          return <span>---</span>;
        return <span>{formatCurrency(row.effectiveCost)}</span>;
      },
    },
    {
      id: "totalCost",
      header: "Total Cost",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#b90200" }}>
              {formatCurrency(row.totalCost)}
            </span>
          );
        return <span>{formatCurrency(row.totalCost)}</span>;
      },
    },
    {
      id: "grossProfit",
      header: "Gross Profit",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {formatCurrency(row.grossProfit)}
            </span>
          );
        return <span>{formatCurrency(row.grossProfit)}</span>;
      },
    },
  ];

  const buildServiceTableRows = (rows: PsPricingRow[]): DataTableRow[] => {
    const result: DataTableRow[] = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.rowType === "data") {
        const subRows: PsPricingRow[] = [];
        let j = i + 1;
        while (j < rows.length && rows[j].rowType === "subservice") {
          subRows.push(rows[j]);
          j++;
        }
        const hasSubtotal = j < rows.length && rows[j].rowType === "subtotal";
        const subtotalRow = hasSubtotal ? rows[j] : null;

        if (subRows.length > 0) {
          const summaryId = `summary-${row.id}`;
          result.push({
            ...(subtotalRow || row),
            id: summaryId,
            resource: row.resource,
            rowType: "data",
            hasDetails: true,
          });

          if (expandedServices[summaryId]) {
            result.push({
              ...row,
              id: `detail-${row.id}`,
              parentServiceId: summaryId,
            });
            for (const sub of subRows) {
              result.push({
                ...sub,
                parentServiceId: summaryId,
              });
            }
          }

          i = hasSubtotal ? j : j - 1;
        } else {
          result.push({ ...row });
        }
      } else if (row.rowType === "total") {
        result.push({ ...row });
      }
    }
    return result;
  };

  const hasDataRows = (rows: PsPricingRow[]) =>
    rows.some((r) => r.rowType === "data" || r.rowType === "subservice");

  const renderSection = (
    key: string,
    title: string,
    rows: PsPricingRow[],
    firstColumnName = "Resource",
    showAdjustment = false,
    isServiceTab = false
  ) => {
    const sectionExpanded = isSectionExpanded(key);
    const tableRows: DataTableRow[] = isServiceTab
      ? buildServiceTableRows(rows)
      : rows;

    return (
      <div key={key} className={styles.section}>
        <div
          className={styles.sectionHeader}
          onClick={() => toggleSection(key)}
        >
          <span className={styles.sectionTitle}>{title}</span>
          <FontAwesomeIcon
            icon={sectionExpanded ? faChevronUp : faChevronDown}
            className={styles.sectionChevron}
          />
        </div>
        {sectionExpanded && (
          <div className={styles.sectionBody}>
            <DataTable
              data={tableRows}
              columns={
                isServiceTab
                  ? buildServiceColumns()
                  : buildColumns(firstColumnName, showAdjustment)
              }
              selectable={false}
              classForTable={styles.sectionTable}
              striped
              hover
            />
          </div>
        )}
      </div>
    );
  };

  // Inject adjustment row into profRows for By LOB
  const profRowsWithAdj = (() => {
    const rows = [...byLobData.profRows];
    if (oneTimeAdjustment !== 0) {
      const totalIdx = rows.findIndex((r) => r.rowType === "total");
      if (totalIdx >= 0) {
        rows.splice(totalIdx, 0, {
          id: "adjustment",
          resource: "Price Adjustment",
          hours: 0,
          rate: 0,
          revenue: oneTimeAdjustment,
          effectiveCost: 0,
          totalCost: 0,
          grossProfit: oneTimeAdjustment,
          rowType: "adjustment",
        });
      }
    }
    return rows;
  })();

  return (
    <div className={`${styles.page} ${styles.columnWidths}`}>
      {handleAlert(
        !!adjustmentError,
        adjustmentError || [],
        () => setAdjustmentError(null),
        "warning",
        ToastAlert
      )}
      {handleAlert(
        !!paymentTermError,
        paymentTermError || [],
        () => setPaymentTermError(null),
        "warning",
        ToastAlert
      )}
      {isLoading ? (
        <ScopeStackSpinner />
      ) : (
        <>
          <SlideoutMenu
            isOpen={showTerms}
            onClose={() => setShowTerms(false)}
            onSave={() => {
              onSubmitPaymentTerm();
              setShowTerms(false);
            }}
            title="Professional Service Terms"
            actionBtnText="Save"
          >
            <Form.Label>Payment Term</Form.Label>
            <div className="formGroup">
              <select
                className="form-control"
                value={newPaymentTermId || ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setNewPaymentTermId(e.target.value)
                }
              >
                {paymentTerms.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.attributes.name}
                  </option>
                ))}
              </select>
            </div>
          </SlideoutMenu>

          <AdminFormPage
            title={
              <>
                Professional Service Pricing
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: "normal",
                    marginTop: "-17px",
                    color: "#1c2655",
                  }}
                >
                  <strong>Payment Term: </strong>
                  {currentPaymentTerm?.attributes?.name || "—"}
                </div>
              </>
            }
            tabs={[
              { key: "byLob", label: "By LOB" },
              { key: "byPhase", label: "By Phase" },
              ...(showServiceTab
                ? [{ key: "byService", label: "By Service" }]
                : []),
              ...(showLocationTab
                ? [{ key: "byLocation", label: "By Location" }]
                : []),
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            extraActions={
              <Button
                className="btnSeawhite"
                onClick={() => setShowTerms(true)}
              >
                Payment Terms
              </Button>
            }
          >
            {activeTab === "byLob" && (
              <div className={styles.tabContent}>
                {hasDataRows(profRowsWithAdj) &&
                  renderSection(
                    "prof-summary",
                    "Professional Service Resource Summary",
                    profRowsWithAdj,
                    "Resource",
                    true
                  )}

                {byLobData.lobTables
                  .filter((t) => hasDataRows(t.rows))
                  .map((t) => renderSection(t.title, t.title, t.rows))}

                {hasDataRows(byLobData.govRows) &&
                  renderSection(
                    "governance",
                    "Project Governance Pricing",
                    byLobData.govRows
                  )}
              </div>
            )}

            {activeTab === "byPhase" && (
              <div className={styles.tabContent}>
                {byPhaseData.phaseTables
                  .filter((t) => hasDataRows(t.rows))
                  .map((t) => renderSection(t.title, t.title, t.rows))}

                {hasDataRows(byPhaseData.govRows) &&
                  renderSection(
                    "governance",
                    "Project Governance Pricing",
                    byPhaseData.govRows
                  )}
              </div>
            )}

            {activeTab === "byService" && showServiceTab && (
              <div className={styles.tabContent}>
                {byServiceData.phaseTables
                  .filter((t) => hasDataRows(t.rows))
                  .map((t) =>
                    renderSection(
                      t.title,
                      t.title,
                      t.rows,
                      "Service",
                      false,
                      true
                    )
                  )}

                {hasDataRows(byServiceData.govRows) &&
                  renderSection(
                    "governance",
                    "Project Governance Pricing",
                    byServiceData.govRows
                  )}
              </div>
            )}

            {activeTab === "byLocation" && showLocationTab && (
              <div className={styles.tabContent}>
                {byLocationData.locTables
                  .filter((t) => hasDataRows(t.rows))
                  .map((t) => renderSection(t.title, t.title, t.rows))}

                {hasDataRows(byLobData.govRows) &&
                  renderSection(
                    "governance",
                    "Project Governance Pricing",
                    byLobData.govRows
                  )}
              </div>
            )}
          </AdminFormPage>
        </>
      )}
    </div>
  );
};

export default Show;
